import { lazy, Suspense, useEffect, type ComponentType } from "react"
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { AuthProvider } from "@/components/AuthProvider"
import { ProtectedRoute } from "@/components/ProtectedRoute"
import { PermissionRoute } from "@/components/PermissionRoute"
import { PortalProtectedRoute } from "@/components/PortalProtectedRoute"
import { RootRedirect } from "@/components/RootRedirect"
import { BarePathRedirect } from "@/components/BarePathRedirect"
import { APP_PATH_PREFIXES } from "@/lib/inject-org-slug"
import { reloadOnceForStaleChunk } from "@/lib/chunk-reload"
import { shouldRetryQuery } from "@/lib/query-retry"
import { buildMutationCache } from "@/lib/mutation-cache-policy"
import { Toaster } from "@/components/ui/toaster"
import { PlanLimitDialogHost } from "@/components/onboarding/PlanLimitDialogHost"
import { ThemeProvider } from "@/components/ThemeProvider"
import { RouteTitle } from "@/components/RouteTitle"
import { ProgressToastContainer } from "@/components/onboarding/ProgressToast"
import { RouteErrorBoundary } from "@/components/RouteErrorBoundary"
import { RouteLoadingFallback } from "@/components/RouteLoadingFallback"

// Layouts en EAGER : chargés avec le bundle entry. Si on les lazy-load, chaque
// navigation auth paye le coût de fetch du chunk → flakiness E2E (timeouts à 5s
// sur les expect(...).toBeVisible). Le gain de bundle public est conservé via
// le manualChunks "app-public" (PublicQuotePage + AuthLayout) qui split de toute
// façon AuthLayout dans son propre chunk.
import { AuthLayout } from "@/layouts/AuthLayout"
import { DashboardLayout } from "@/layouts/DashboardLayout"
import { PortalLayout } from "@/layouts/PortalLayout"

const lazyNamed = <K extends string>(
  loader: () => Promise<Record<K, ComponentType<unknown>>>,
  name: K,
) =>
  lazy(() =>
    loader()
      .then((m) => ({ default: m[name] }))
      .catch((error: unknown) => {
        // Chunk périmé après un déploiement. Rechargement unique, page en attente.
        if (reloadOnceForStaleChunk(error)) return new Promise<never>(() => undefined)
        throw error
      }),
  )

// Auth pages
const LoginPage = lazyNamed(() => import("@/pages/auth/LoginPage"), "LoginPage")
const RegisterPage = lazyNamed(() => import("@/pages/auth/RegisterPage"), "RegisterPage")
const ForgotPasswordPage = lazyNamed(() => import("@/pages/auth/ForgotPasswordPage"), "ForgotPasswordPage")
const ResetPasswordPage = lazyNamed(() => import("@/pages/auth/ResetPasswordPage"), "ResetPasswordPage")
const AcceptInvitePage = lazyNamed(() => import("@/pages/auth/AcceptInvitePage"), "AcceptInvitePage")
const OAuthConsentPage = lazyNamed(() => import("@/pages/auth/OAuthConsentPage"), "OAuthConsentPage")
const EmailVerificationPage = lazyNamed(() => import("@/pages/auth/EmailVerificationPage"), "EmailVerificationPage")

// Onboarding pages
const QuestionnairePage = lazyNamed(() => import("@/pages/onboarding/QuestionnairePage"), "QuestionnairePage")

// Main pages
const DashboardPage = lazyNamed(() => import("@/pages/DashboardPage"), "DashboardPage")
const TasksPage = lazyNamed(() => import("@/pages/TasksPage"), "TasksPage")

// CRM pages
const AccountsPage = lazyNamed(() => import("@/pages/crm/AccountsPage"), "AccountsPage")
const AccountDetailPage = lazyNamed(() => import("@/pages/crm/AccountDetailPage"), "AccountDetailPage")
const ContactsPage = lazyNamed(() => import("@/pages/crm/ContactsPage"), "ContactsPage")
const ContactDetailPage = lazyNamed(() => import("@/pages/crm/ContactDetailPage"), "ContactDetailPage")
const OpportunitiesPage = lazyNamed(() => import("@/pages/crm/OpportunitiesPage"), "OpportunitiesPage")
const OpportunityDetailPage = lazyNamed(() => import("@/pages/crm/OpportunityDetailPage"), "OpportunityDetailPage")
const NeedsAssessmentsPage = lazyNamed(() => import("@/pages/crm/NeedsAssessmentsPage"), "NeedsAssessmentsPage")
const NeedsAssessmentDetailPage = lazyNamed(() => import("@/pages/crm/NeedsAssessmentDetailPage"), "NeedsAssessmentDetailPage")

// Training pages
const ProgramsPage = lazyNamed(() => import("@/pages/training/ProgramsPage"), "ProgramsPage")
const ProgramDetailPage = lazyNamed(() => import("@/pages/training/ProgramDetailPage"), "ProgramDetailPage")
const SessionsPage = lazyNamed(() => import("@/pages/training/SessionsPage"), "SessionsPage")
const SessionDetailPage = lazyNamed(() => import("@/pages/training/SessionDetailPage"), "SessionDetailPage")
const SessionChecklistPage = lazyNamed(() => import("@/pages/training/SessionChecklistPage"), "SessionChecklistPage")
const SessionDashboardPage = lazyNamed(() => import("@/pages/training/SessionDashboardPage"), "SessionDashboardPage")
const RegistrationsPage = lazyNamed(() => import("@/pages/training/RegistrationsPage"), "RegistrationsPage")
const RegistrationDetailPage = lazyNamed(() => import("@/pages/training/RegistrationDetailPage"), "RegistrationDetailPage")
const AttendancePage = lazyNamed(() => import("@/pages/training/AttendancePage"), "AttendancePage")
const JuriesPage = lazyNamed(() => import("@/pages/training/JuriesPage"), "JuriesPage")
const AuditFolderPage = lazyNamed(() => import("@/pages/training/AuditFolderPage"), "AuditFolderPage")
const CompetencyBlocksPage = lazyNamed(() => import("@/pages/training/CompetencyBlocksPage"), "CompetencyBlocksPage")
const ProgramCompetencyMatrixPage = lazyNamed(() => import("@/pages/training/ProgramCompetencyMatrixPage"), "ProgramCompetencyMatrixPage")
const ProgramNeedsCapturesPage = lazy(() => import("@/pages/training/ProgramNeedsCapturesPage"))
const EnrollmentPipelinePage = lazyNamed(() => import("@/pages/training/EnrollmentPipelinePage"), "EnrollmentPipelinePage")
const EnrollmentDetailPage = lazyNamed(() => import("@/pages/training/EnrollmentDetailPage"), "EnrollmentDetailPage")
const VaePage = lazyNamed(() => import("@/pages/training/VaePage"), "VaePage")
const CalendarPage = lazyNamed(() => import("@/pages/training/CalendarPage"), "CalendarPage")
const DropoutsPage = lazyNamed(() => import("@/pages/training/DropoutsPage"), "DropoutsPage")

// Commercial pages
const CommercialDashboardPage = lazyNamed(() => import("@/pages/commercial/CommercialDashboardPage"), "CommercialDashboardPage")
const QuotesPage = lazyNamed(() => import("@/pages/commercial/QuotesPage"), "QuotesPage")
const QuoteNewPage = lazyNamed(() => import("@/pages/commercial/QuoteNewPage"), "QuoteNewPage")
const QuoteDetailPage = lazyNamed(() => import("@/pages/commercial/QuoteDetailPage"), "QuoteDetailPage")
const QuoteAmendmentDetailPage = lazyNamed(() => import("@/pages/commercial/QuoteAmendmentDetailPage"), "QuoteAmendmentDetailPage")
const InvoicesPage = lazyNamed(() => import("@/pages/commercial/InvoicesPage"), "InvoicesPage")
const InvoiceDetailPage = lazyNamed(() => import("@/pages/commercial/InvoiceDetailPage"), "InvoiceDetailPage")
const ContractsPage = lazyNamed(() => import("@/pages/commercial/ContractsPage"), "ContractsPage")
const ContractDetailPage = lazyNamed(() => import("@/pages/commercial/ContractDetailPage"), "ContractDetailPage")
const OPCOFilesPage = lazyNamed(() => import("@/pages/commercial/OPCOFilesPage"), "OPCOFilesPage")
const OpcoDirectoryPage = lazyNamed(() => import("@/pages/commercial/OpcoDirectoryPage"), "OpcoDirectoryPage")
const OpcoFileDetailPage = lazyNamed(() => import("@/pages/commercial/OpcoFileDetailPage"), "OpcoFileDetailPage")

// Qualiopi pages
const IndicatorsPage = lazyNamed(() => import("@/pages/qualiopi/IndicatorsPage"), "IndicatorsPage")
const IndicatorDetailPage = lazyNamed(() => import("@/pages/qualiopi/IndicatorDetailPage"), "IndicatorDetailPage")
const IndicatorReviewPage = lazyNamed(() => import("@/pages/qualiopi/IndicatorReviewPage"), "IndicatorReviewPage")
const ComplaintsPage = lazyNamed(() => import("@/pages/qualiopi/ComplaintsPage"), "ComplaintsPage")
const ComplaintDetailPage = lazyNamed(() => import("@/pages/qualiopi/ComplaintDetailPage"), "ComplaintDetailPage")
const ImprovementsPage = lazyNamed(() => import("@/pages/qualiopi/ImprovementsPage"), "ImprovementsPage")
const VenuesPage = lazyNamed(() => import("@/pages/qualiopi/VenuesPage"), "VenuesPage")
const EvaluationsPage = lazyNamed(() => import("@/pages/qualiopi/EvaluationsPage"), "EvaluationsPage")
const EvaluationDetailPage = lazyNamed(() => import("@/pages/qualiopi/EvaluationDetailPage"), "EvaluationDetailPage")
const EvaluationTemplateDetailPage = lazyNamed(() => import("@/pages/qualiopi/EvaluationTemplateDetailPage"), "EvaluationTemplateDetailPage")
const EvaluationTemplateQuestionDeletePage = lazyNamed(() => import("@/pages/qualiopi/EvaluationTemplateQuestionDeletePage"), "EvaluationTemplateQuestionDeletePage")
const WatchPage = lazyNamed(() => import("@/pages/qualiopi/WatchPage"), "WatchPage")
const SurveyTemplatesPage = lazyNamed(() => import("@/pages/qualiopi/SurveyTemplatesPage"), "SurveyTemplatesPage")
const SurveyTemplateDetailPage = lazyNamed(() => import("@/pages/qualiopi/SurveyTemplateDetailPage"), "SurveyTemplateDetailPage")
const SurveyAssignmentsPage = lazyNamed(() => import("@/pages/qualiopi/SurveyAssignmentsPage"), "SurveyAssignmentsPage")
const SimulatorPage = lazyNamed(() => import("@/pages/qualiopi/SimulatorPage"), "SimulatorPage")
const QualiopiMarketingKPIsPage = lazyNamed(() => import("@/pages/qualiopi/MarketingKPIsPage"), "MarketingKPIsPage")

// Documents page
const DocumentsPage = lazyNamed(() => import("@/pages/DocumentsPage"), "DocumentsPage")
const DocumentGeneratePage = lazyNamed(() => import("@/pages/DocumentGeneratePage"), "DocumentGeneratePage")

// HR pages
const StaffPage = lazyNamed(() => import("@/pages/hr/StaffPage"), "StaffPage")
const StaffDetailPage = lazyNamed(() => import("@/pages/hr/StaffDetailPage"), "StaffDetailPage")
const OrgChartPage = lazyNamed(() => import("@/pages/hr/OrgChartPage"), "OrgChartPage")
const InstructorsPage = lazyNamed(() => import("@/pages/hr/InstructorsPage"), "InstructorsPage")
const VendorsPage = lazyNamed(() => import("@/pages/hr/VendorsPage"), "VendorsPage")
const VendorWizardPage = lazyNamed(() => import("@/pages/hr/VendorWizardPage"), "VendorWizardPage")
const VendorDetailPage = lazyNamed(() => import("@/pages/hr/VendorDetailPage"), "VendorDetailPage")
const VendorContractsPage = lazyNamed(() => import("@/pages/hr/VendorContractsPage"), "VendorContractsPage")
const VendorContractDetailPage = lazyNamed(() => import("@/pages/hr/VendorContractDetailPage"), "VendorContractDetailPage")
const InstructorDetailPage = lazyNamed(() => import("@/pages/hr/InstructorDetailPage"), "InstructorDetailPage")
const SelfVerificationPage = lazyNamed(() => import("@/pages/hr/SelfVerificationPage"), "SelfVerificationPage")
const SelfVerificationDetailPage = lazyNamed(() => import("@/pages/hr/SelfVerificationDetailPage"), "SelfVerificationDetailPage")
const StaffEvaluationsPage = lazyNamed(() => import("@/pages/hr/StaffEvaluationsPage"), "StaffEvaluationsPage")
const StaffEvaluationDetailPage = lazyNamed(() => import("@/pages/hr/StaffEvaluationDetailPage"), "StaffEvaluationDetailPage")
const ProfessionalInterviewsPage = lazyNamed(() => import("@/pages/hr/ProfessionalInterviewsPage"), "ProfessionalInterviewsPage")

// Messaging pages
const MessagesPage = lazyNamed(() => import("@/pages/messaging/MessagesPage"), "MessagesPage")

// Admin pages
const EmailTemplatesPage = lazyNamed(() => import("@/pages/admin/EmailTemplatesPage"), "EmailTemplatesPage")
const ScheduledJobsPage = lazyNamed(() => import("@/pages/admin/ScheduledJobsPage"), "ScheduledJobsPage")
const ExportsPage = lazyNamed(() => import("@/pages/admin/ExportsPage"), "ExportsPage")
const AuditLogPage = lazyNamed(() => import("@/pages/admin/AuditLogPage"), "AuditLogPage")
const DocumentTemplatesPage = lazyNamed(() => import("@/pages/admin/DocumentTemplatesPage"), "DocumentTemplatesPage")
const MarketingKPIsPage = lazyNamed(() => import("@/pages/admin/MarketingKPIsPage"), "MarketingKPIsPage")
const CompetencyFrameworksPage = lazyNamed(() => import("@/pages/admin/CompetencyFrameworksPage"), "CompetencyFrameworksPage")
const CpfValidationPage = lazyNamed(() => import("@/pages/admin/CpfValidationPage"), "CpfValidationPage")
const PublicCatalogPage = lazyNamed(() => import("@/pages/admin/PublicCatalogPage"), "PublicCatalogPage")
const DisabilityOrganizationsPage = lazyNamed(() => import("@/pages/admin/DisabilityOrganizationsPage"), "DisabilityOrganizationsPage")
const DisabilityOrganizationDeletePage = lazyNamed(() => import("@/pages/admin/DisabilityOrganizationDeletePage"), "DisabilityOrganizationDeletePage")
const EmailAuditLogPage = lazyNamed(() => import("@/pages/admin/EmailAuditLogPage"), "EmailAuditLogPage")
const EmailOutboxPage = lazyNamed(() => import("@/pages/admin/EmailOutboxPage"), "EmailOutboxPage")

// Help page
const HelpPage = lazyNamed(() => import("@/pages/help/HelpPage"), "HelpPage")

// Settings pages
const SettingsPage = lazyNamed(() => import("@/pages/settings/SettingsPage"), "SettingsPage")
const OpcoRulesPage = lazyNamed(() => import("@/pages/settings/OpcoRulesPage"), "OpcoRulesPage")

// Reports pages
const BPFReportPage = lazyNamed(() => import("@/pages/reports/BPFReportPage"), "BPFReportPage")
const StatisticsPage = lazyNamed(() => import("@/pages/reports/StatisticsPage"), "StatisticsPage")

// Portal pages
const PortalLoginPage = lazyNamed(() => import("@/pages/portal/PortalLoginPage"), "PortalLoginPage")
const PortalDashboardPage = lazyNamed(() => import("@/pages/portal/PortalDashboardPage"), "PortalDashboardPage")
const PortalSessionsPage = lazyNamed(() => import("@/pages/portal/PortalSessionsPage"), "PortalSessionsPage")
const PortalSessionDetailPage = lazyNamed(() => import("@/pages/portal/PortalSessionDetailPage"), "PortalSessionDetailPage")
const PortalAttendancePage = lazyNamed(() => import("@/pages/portal/PortalAttendancePage"), "PortalAttendancePage")
const PortalAttendanceSignPage = lazyNamed(() => import("@/pages/portal/PortalAttendanceSignPage"), "PortalAttendanceSignPage")
const PortalEvaluationsPage = lazyNamed(() => import("@/pages/portal/PortalEvaluationsPage"), "PortalEvaluationsPage")
const PortalDocumentsPage = lazyNamed(() => import("@/pages/portal/PortalDocumentsPage"), "PortalDocumentsPage")
const PortalProfilePage = lazyNamed(() => import("@/pages/portal/PortalProfilePage"), "PortalProfilePage")
const PortalHealthConsentWithdrawPage = lazyNamed(() => import("@/pages/portal/PortalHealthConsentWithdrawPage"), "PortalHealthConsentWithdrawPage")

// Jury Portal (public, token-based access)
const JuryPortalPage = lazyNamed(() => import("@/pages/jury/JuryPortalPage"), "JuryPortalPage")

// Suppressions : une page de confirmation par ressource.
const AccountDeletePage = lazyNamed(() => import("@/pages/crm/AccountDeletePage"), "AccountDeletePage")
const ContactDeletePage = lazyNamed(() => import("@/pages/crm/ContactDeletePage"), "ContactDeletePage")
const AccessibilityNeedDeletePage = lazyNamed(() => import("@/pages/crm/AccessibilityNeedDeletePage"), "AccessibilityNeedDeletePage")
const HealthConsentWithdrawPage = lazyNamed(() => import("@/pages/crm/HealthConsentWithdrawPage"), "HealthConsentWithdrawPage")
const AccommodationDeletePage = lazyNamed(() => import("@/pages/crm/AccommodationDeletePage"), "AccommodationDeletePage")
const DisabilityReferralDeletePage = lazyNamed(() => import("@/pages/crm/DisabilityReferralDeletePage"), "DisabilityReferralDeletePage")
const OpportunityDeletePage = lazyNamed(() => import("@/pages/crm/OpportunityDeletePage"), "OpportunityDeletePage")
const NeedsAssessmentDeletePage = lazyNamed(() => import("@/pages/crm/NeedsAssessmentDeletePage"), "NeedsAssessmentDeletePage")
const ProgramDeletePage = lazyNamed(() => import("@/pages/training/ProgramDeletePage"), "ProgramDeletePage")
const ProgramModuleDeletePage = lazyNamed(() => import("@/pages/training/ProgramModuleDeletePage"), "ProgramModuleDeletePage")
const ProgramDeliverableDeletePage = lazyNamed(() => import("@/pages/training/ProgramDeliverableDeletePage"), "ProgramDeliverableDeletePage")
const ProgramVersionDeletePage = lazyNamed(() => import("@/pages/training/ProgramVersionDeletePage"), "ProgramVersionDeletePage")
const SessionDeletePage = lazyNamed(() => import("@/pages/training/SessionDeletePage"), "SessionDeletePage")
const SessionSlotDeletePage = lazyNamed(() => import("@/pages/training/SessionSlotDeletePage"), "SessionSlotDeletePage")
const SessionDocumentDeletePage = lazyNamed(() => import("@/pages/training/SessionDocumentDeletePage"), "SessionDocumentDeletePage")
const SessionDeliverableUncheckPage = lazyNamed(() => import("@/pages/training/SessionDeliverableUncheckPage"), "SessionDeliverableUncheckPage")
const RegistrationDeletePage = lazyNamed(() => import("@/pages/training/RegistrationDeletePage"), "RegistrationDeletePage")
const RegistrationProofDeletePage = lazyNamed(() => import("@/pages/training/RegistrationProofDeletePage"), "RegistrationProofDeletePage")
const VaeJourneyDeletePage = lazyNamed(() => import("@/pages/training/VaeJourneyDeletePage"), "VaeJourneyDeletePage")
const CompetencyBlockDeletePage = lazyNamed(() => import("@/pages/training/CompetencyBlockDeletePage"), "CompetencyBlockDeletePage")
const JuryDeletePage = lazyNamed(() => import("@/pages/training/JuryDeletePage"), "JuryDeletePage")
const JuryMemberDeletePage = lazyNamed(() => import("@/pages/training/JuryMemberDeletePage"), "JuryMemberDeletePage")
const ContactErasurePage = lazyNamed(() => import("@/pages/crm/ContactErasurePage"), "ContactErasurePage")
const InstructorErasurePage = lazyNamed(() => import("@/pages/hr/InstructorErasurePage"), "InstructorErasurePage")
const JuryMemberErasurePage = lazyNamed(() => import("@/pages/training/JuryMemberErasurePage"), "JuryMemberErasurePage")
const QuoteDeletePage = lazyNamed(() => import("@/pages/commercial/QuoteDeletePage"), "QuoteDeletePage")
const QuoteLineDeletePage = lazyNamed(() => import("@/pages/commercial/QuoteLineDeletePage"), "QuoteLineDeletePage")
const QuoteNeedsEntryDeletePage = lazyNamed(() => import("@/pages/commercial/QuoteNeedsEntryDeletePage"), "QuoteNeedsEntryDeletePage")
const QuoteAmendmentLineDeletePage = lazyNamed(() => import("@/pages/commercial/QuoteAmendmentLineDeletePage"), "QuoteAmendmentLineDeletePage")
const InvoiceDeletePage = lazyNamed(() => import("@/pages/commercial/InvoiceDeletePage"), "InvoiceDeletePage")
const ContractDeletePage = lazyNamed(() => import("@/pages/commercial/ContractDeletePage"), "ContractDeletePage")
const ContractPartyDeletePage = lazyNamed(() => import("@/pages/commercial/ContractPartyDeletePage"), "ContractPartyDeletePage")
const ComplaintDeletePage = lazyNamed(() => import("@/pages/qualiopi/ComplaintDeletePage"), "ComplaintDeletePage")
const ImprovementActionDeletePage = lazyNamed(() => import("@/pages/qualiopi/ImprovementActionDeletePage"), "ImprovementActionDeletePage")
const WatchEntryDeletePage = lazyNamed(() => import("@/pages/qualiopi/WatchEntryDeletePage"), "WatchEntryDeletePage")
const WatchSourceDeletePage = lazyNamed(() => import("@/pages/qualiopi/WatchSourceDeletePage"), "WatchSourceDeletePage")
const SurveyTemplateDeletePage = lazyNamed(() => import("@/pages/qualiopi/SurveyTemplateDeletePage"), "SurveyTemplateDeletePage")
const SurveyQuestionDeletePage = lazyNamed(() => import("@/pages/qualiopi/SurveyQuestionDeletePage"), "SurveyQuestionDeletePage")
const VenueDeletePage = lazyNamed(() => import("@/pages/qualiopi/VenueDeletePage"), "VenueDeletePage")
const DocumentDeletePage = lazyNamed(() => import("@/pages/DocumentDeletePage"), "DocumentDeletePage")
const SelfVerificationDeletePage = lazyNamed(() => import("@/pages/hr/SelfVerificationDeletePage"), "SelfVerificationDeletePage")
const InstructorPhotoDeletePage = lazyNamed(() => import("@/pages/hr/InstructorPhotoDeletePage"), "InstructorPhotoDeletePage")
const InstructorDocumentDeletePage = lazyNamed(() => import("@/pages/hr/InstructorDocumentDeletePage"), "InstructorDocumentDeletePage")
const InstructorAvailabilitySlotDeletePage = lazyNamed(() => import("@/pages/hr/InstructorAvailabilitySlotDeletePage"), "InstructorAvailabilitySlotDeletePage")
const InstructorSkillDeletePage = lazyNamed(() => import("@/pages/hr/InstructorSkillDeletePage"), "InstructorSkillDeletePage")
const VendorDocumentDeletePage = lazyNamed(() => import("@/pages/hr/VendorDocumentDeletePage"), "VendorDocumentDeletePage")
const VendorInstructorDeletePage = lazyNamed(() => import("@/pages/hr/VendorInstructorDeletePage"), "VendorInstructorDeletePage")
const ProgramIllustrationDeletePage = lazyNamed(() => import("@/pages/training/ProgramIllustrationDeletePage"), "ProgramIllustrationDeletePage")
const ProgramBrochureDeletePage = lazyNamed(() => import("@/pages/training/ProgramBrochureDeletePage"), "ProgramBrochureDeletePage")
const ProgramSuccessCriterionDeletePage = lazyNamed(() => import("@/pages/training/ProgramSuccessCriterionDeletePage"), "ProgramSuccessCriterionDeletePage")
const StaffEvaluationDeletePage = lazyNamed(() => import("@/pages/hr/StaffEvaluationDeletePage"), "StaffEvaluationDeletePage")
const StaffDeletePage = lazyNamed(() => import("@/pages/hr/StaffDeletePage"), "StaffDeletePage")
const MissionDeletePage = lazyNamed(() => import("@/pages/hr/MissionDeletePage"), "MissionDeletePage")
const VendorDeletePage = lazyNamed(() => import("@/pages/hr/VendorDeletePage"), "VendorDeletePage")
const VendorContractDeletePage = lazyNamed(() => import("@/pages/hr/VendorContractDeletePage"), "VendorContractDeletePage")
const DocumentTemplateDeletePage = lazyNamed(() => import("@/pages/admin/DocumentTemplateDeletePage"), "DocumentTemplateDeletePage")
const EmailTemplateDeletePage = lazyNamed(() => import("@/pages/admin/EmailTemplateDeletePage"), "EmailTemplateDeletePage")
const EmailTemplateRestorePage = lazyNamed(() => import("@/pages/admin/EmailTemplateRestorePage"), "EmailTemplateRestorePage")
const CompetencyFrameworkDeletePage = lazyNamed(() => import("@/pages/admin/CompetencyFrameworkDeletePage"), "CompetencyFrameworkDeletePage")
const MarketingKpiDeletePage = lazyNamed(() => import("@/pages/admin/MarketingKpiDeletePage"), "MarketingKpiDeletePage")
const ExportDeletePage = lazyNamed(() => import("@/pages/admin/ExportDeletePage"), "ExportDeletePage")
const OpcoRuleDeletePage = lazyNamed(() => import("@/pages/settings/OpcoRuleDeletePage"), "OpcoRuleDeletePage")
const TeamMemberDeletePage = lazyNamed(() => import("@/pages/settings/TeamMemberDeletePage"), "TeamMemberDeletePage")
const OrganizationTerminationPage = lazyNamed(() => import("@/pages/settings/OrganizationTerminationPage"), "OrganizationTerminationPage")
const SignatureAssetDeletePage = lazyNamed(() => import("@/pages/settings/SignatureAssetDeletePage"), "SignatureAssetDeletePage")
const OrganizationTerminationDeletePage = lazyNamed(
  () => import("@/pages/settings/OrganizationTerminationDeletePage"),
  "OrganizationTerminationDeletePage",
)

// Public Catalog (no auth required) — la page catalogue est servie côté backend
// (SSR Tera sur /c/:tenantSlug). L'ancienne route SPA /public/:slug/catalog
// redirige vers /c/:slug (cf. <Route> plus bas).
const PublicQuotePage = lazy(() => import("@/pages/public/PublicQuotePage"))
const PublicQuoteAmendmentPage = lazy(() => import("@/pages/public/PublicQuoteAmendmentPage"))
const PublicSurveyPage = lazy(() => import("@/pages/public/PublicSurveyPage"))
const PublicProgramPage = lazy(() => import("@/pages/public/PublicProgramPage"))
const PublicInvoicePage = lazy(() => import("@/pages/public/PublicInvoicePage"))
const PublicNeedsFormPage = lazy(() => import("@/pages/public/PublicNeedsFormPage"))
const PublicJobDescriptionPage = lazy(() => import("@/pages/public/PublicJobDescriptionPage"))
const PublicVendorContractPage = lazy(() => import("@/pages/public/PublicVendorContractPage"))
const VerifyVendorContractPage = lazy(() => import("@/pages/public/VerifyVendorContractPage"))
const PublicAttendanceSignPage = lazy(() => import("@/pages/public/PublicAttendanceSignPage"))
const PublicInstructorInvitationPage = lazy(() => import("@/pages/public/PublicInstructorInvitationPage"))

// 404 page
const NotFoundPage = lazyNamed(() => import("@/pages/NotFoundPage"), "NotFoundPage")

// Type annoté explicitement : la policy MutationCache référence `queryClient`
// via une closure paresseuse (buildMutationCache(() => queryClient)), donc son
// initialiseur se référence lui-même — sans l'annotation, TS ne peut pas
// inférer le type (TS7022 « referenced directly or indirectly in its own
// initializer »).
const queryClient: QueryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: shouldRetryQuery,
      refetchOnWindowFocus: true,
    },
  },
  // Garantie structurelle anti-UI-périmée : toute mutation créée via
  // useApiMutation (src/hooks/use-api-mutation.ts) pose meta.entity sur les
  // racines de clé qu'elle affecte. La policy (partagée avec le property test
  // via src/lib/mutation-cache-policy.ts) invalide toutes les queries
  // correspondantes à chaque succès, donc les pages n'ont jamais à invalider à
  // la main les caches dont d'autres pages dépendent.
  mutationCache: buildMutationCache(() => queryClient),
})

/**
 * Redirige l'ancienne route SPA /public/:slug/catalog vers /c/:slug, servie en
 * SSR par le backend (hors React Router). On utilise window.location.replace
 * plutôt que <Navigate> car la cible n'est PAS une route SPA — un Navigate
 * resterait dans le bundle React et tomberait sur la 404 SPA.
 */
function LegacyCatalogRedirect() {
  const { slug } = useParams<{ slug: string }>()
  useEffect(() => {
    window.location.replace(slug ? `/c/${slug}` : "/")
  }, [slug])
  return <RouteLoadingFallback />
}

function App() {
  return (
    <ThemeProvider>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <RouteTitle />
          {/* Global ErrorBoundary : filet de sécurité ultime (issue #38). Tout
              crash render qui n'est pas catché par un boundary plus interne
              tombe ici. Affiche la page d'erreur friendly avec errorId au lieu
              de la page blanche silencieuse historique. */}
          <RouteErrorBoundary>
          <Suspense fallback={<RouteLoadingFallback />}>
          <Routes>
            {/* Public auth routes (no org prefix) */}
            <Route element={<AuthLayout />}>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/accept-invite" element={<AcceptInvitePage />} />
              <Route path="/verify-email" element={<EmailVerificationPage />} />
            </Route>

            {/* Onboarding questionnaire (post-login, pre-dashboard) */}
            <Route path="/onboarding/questionnaire" element={<QuestionnairePage />} />

            {/* Authenticated routes with org prefix */}
            <Route
              path="/:orgSlug"
              element={
                <ProtectedRoute>
                  <DashboardLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="dashboard" replace />} />
              <Route path="dashboard" element={<DashboardPage />} />
              {/* Ouverte à tout membre connecté.
                  Le serveur limite « Mes tâches » au membre.
                  « Toutes les tâches » exige le droit Rapports. */}
              <Route path="tasks" element={<TasksPage />} />

              {/* CRM */}
              <Route element={<PermissionRoute module="crm" />}>
                <Route path="crm/accounts" element={<AccountsPage />} />
                <Route path="crm/accounts/:id" element={<AccountDetailPage />} />
                <Route path="crm/contacts" element={<ContactsPage />} />
                <Route path="crm/contacts/:id" element={<ContactDetailPage />} />
                <Route path="crm/opportunities" element={<OpportunitiesPage />} />
                <Route path="crm/opportunities/:id" element={<OpportunityDetailPage />} />
                <Route path="crm/needs-assessments" element={<NeedsAssessmentsPage />} />
                <Route path="crm/needs-assessments/:id" element={<NeedsAssessmentDetailPage />} />
                <Route element={<PermissionRoute module="crm" operation="delete" />}>
                  <Route path="crm/accounts/:id/delete" element={<AccountDeletePage />} />
                  <Route path="crm/contacts/:id/delete" element={<ContactDeletePage />} />
                  <Route path="crm/contacts/:id/accessibility/needs/:needId/delete" element={<AccessibilityNeedDeletePage />} />
                  <Route path="crm/contacts/:id/accessibility/accommodations/:accommodationId/delete" element={<AccommodationDeletePage />} />
                  <Route path="crm/contacts/:id/accessibility/disability-referrals/:referralId/delete" element={<DisabilityReferralDeletePage />} />
                  <Route path="crm/opportunities/:id/delete" element={<OpportunityDeletePage />} />
                  <Route path="crm/needs-assessments/:id/delete" element={<NeedsAssessmentDeletePage />} />
                </Route>
                {/* Retrait du consentement santé : CRM › Modifier, lecteur vérifié au serveur. */}
                <Route element={<PermissionRoute module="crm" operation="write" />}>
                  <Route path="crm/contacts/:id/accessibility/health-consent/withdraw" element={<HealthConsentWithdrawPage />} />
                </Route>
                {/* Organismes handicap : droits CRM du serveur, adresse sous admin/. */}
                <Route path="admin/disability-organizations" element={<DisabilityOrganizationsPage />} />
                <Route element={<PermissionRoute module="crm" operation="delete" />}>
                  <Route path="admin/disability-organizations/:id/delete" element={<DisabilityOrganizationDeletePage />} />
                </Route>
              </Route>

              {/* Training */}
              <Route element={<PermissionRoute module="training" />}>
                <Route path="training/programs" element={<ProgramsPage />} />
                <Route path="training/programs/:id" element={<ProgramDetailPage />} />
                <Route path="training/programs/:id/competencies" element={<ProgramCompetencyMatrixPage />} />
                <Route path="training/programs/:id/needs-captures" element={<ProgramNeedsCapturesPage />} />
                <Route path="training/sessions" element={<SessionsPage />} />
                <Route path="training/sessions/:id" element={<SessionDetailPage />} />
                <Route path="training/sessions/:id/checklist" element={<SessionChecklistPage />} />
                <Route path="training/sessions/:id/dashboard" element={<SessionDashboardPage />} />
                <Route path="training/registrations" element={<RegistrationsPage />} />
                <Route path="training/registrations/:id" element={<RegistrationDetailPage />} />
                <Route path="training/registrations/:id/audit-folder" element={<AuditFolderPage />} />
                <Route path="training/attendance" element={<AttendancePage />} />
                {/* Les jurys exigent la lecture du module Jury (un rôle peut avoir
                    Formation sans Jury) : garde interne en plus de la garde Formation. */}
                <Route element={<PermissionRoute module="jury" />}>
                  <Route path="training/juries" element={<JuriesPage />} />
                </Route>
                <Route path="training/enrollment-pipeline" element={<EnrollmentPipelinePage />} />
                <Route path="training/enrollments/:id" element={<EnrollmentDetailPage />} />
                <Route path="training/competency-blocks" element={<CompetencyBlocksPage />} />
                <Route path="training/vae" element={<VaePage />} />
                <Route path="training/calendar" element={<CalendarPage />} />
                <Route path="training/dropouts" element={<DropoutsPage />} />
                {/* Illustration, brochure, décoche : l'écriture suffit côté serveur. */}
                <Route element={<PermissionRoute module="training" operation="write" />}>
                  <Route path="training/programs/:id/illustration/delete" element={<ProgramIllustrationDeletePage />} />
                  <Route path="training/sessions/:id/checklist/deliverables/:deliverableId/uncheck" element={<SessionDeliverableUncheckPage />} />
                  <Route path="training/programs/:id/brochure/delete" element={<ProgramBrochureDeletePage />} />
                </Route>
                <Route element={<PermissionRoute module="training" operation="delete" />}>
                  <Route path="training/programs/:id/delete" element={<ProgramDeletePage />} />
                  <Route path="training/programs/:id/modules/:moduleId/delete" element={<ProgramModuleDeletePage />} />
                  <Route path="training/programs/:id/deliverables/:deliverableId/delete" element={<ProgramDeliverableDeletePage />} />
                  <Route path="training/programs/:id/versions/:versionId/delete" element={<ProgramVersionDeletePage />} />
                  <Route path="training/programs/:id/success-criteria/:criterionId/delete" element={<ProgramSuccessCriterionDeletePage />} />
                  <Route path="training/sessions/:id/delete" element={<SessionDeletePage />} />
                  <Route path="training/sessions/:id/slots/:slotId/delete" element={<SessionSlotDeletePage />} />
                  <Route path="training/sessions/:id/documents/:docId/delete" element={<SessionDocumentDeletePage />} />
                  <Route path="training/registrations/:id/delete" element={<RegistrationDeletePage />} />
                  <Route path="training/registrations/:id/audit-folder/proofs/:proofId/delete" element={<RegistrationProofDeletePage />} />
                  <Route path="training/vae/:id/delete" element={<VaeJourneyDeletePage />} />
                </Route>
              </Route>
              {/* Blocs et jurys : module Jury seul, comme leurs handlers. */}
              <Route element={<PermissionRoute module="jury" operation="delete" />}>
                <Route path="training/competency-blocks/:id/delete" element={<CompetencyBlockDeletePage />} />
                <Route path="training/juries/:id/delete" element={<JuryDeletePage />} />
                <Route path="training/juries/:id/members/:memberId/delete" element={<JuryMemberDeletePage />} />
              </Route>

              {/* Commercial */}
              <Route element={<PermissionRoute module="commercial" />}>
                <Route path="commercial/dashboard" element={<CommercialDashboardPage />} />
                <Route path="commercial/quotes" element={<QuotesPage />} />
                <Route element={<PermissionRoute module="commercial" operation="write" />}>
                  <Route path="commercial/quotes/new" element={<QuoteNewPage />} />
                  {/* Une ligne brouillon se retire avec le droit d'écriture. */}
                  <Route path="commercial/quotes/:id/lines/:lineId/delete" element={<QuoteLineDeletePage />} />
                  <Route path="commercial/quotes/:id/amendments/:aid/lines/:lineId/delete" element={<QuoteAmendmentLineDeletePage />} />
                  {/* Annuler une facture émet un avoir : droit d'écriture, comme create_credit_note. */}
                  <Route path="commercial/invoices/:id/delete" element={<InvoiceDeletePage />} />
                </Route>
                <Route path="commercial/quotes/:id" element={<QuoteDetailPage />} />
                <Route path="commercial/quotes/:id/amendments/:aid" element={<QuoteAmendmentDetailPage />} />
                <Route path="commercial/invoices" element={<InvoicesPage />} />
                <Route path="commercial/invoices/:id" element={<InvoiceDetailPage />} />
                <Route path="commercial/contracts" element={<ContractsPage />} />
                <Route path="commercial/contracts/:id" element={<ContractDetailPage />} />
                <Route path="commercial/opco" element={<OpcoDirectoryPage />} />
                <Route path="commercial/opco-files" element={<OPCOFilesPage />} />
                <Route path="commercial/opco-files/:id" element={<OpcoFileDetailPage />} />
                <Route
                  element={
                    <PermissionRoute
                      // comply-ignore: no-duplicate-string — une garde de route par opération, comme ailleurs.
                      module="commercial"
                      operation="delete"
                    />
                  }
                >
                  <Route path="commercial/quotes/:id/delete" element={<QuoteDeletePage />} />
                  <Route path="commercial/quotes/:id/needs/:entryId/delete" element={<QuoteNeedsEntryDeletePage />} />
                  <Route path="commercial/contracts/:id/delete" element={<ContractDeletePage />} />
                  <Route path="commercial/contracts/:id/parties/:partyId/delete" element={<ContractPartyDeletePage />} />
                </Route>
              </Route>

              {/* Qualiopi */}
              <Route element={<PermissionRoute module="qualiopi" />}>
                <Route path="qualiopi/indicators" element={<IndicatorsPage />} />
                <Route path="qualiopi/indicators/review" element={<IndicatorReviewPage />} />
                <Route path="qualiopi/indicators/:number" element={<IndicatorDetailPage />} />
                <Route path="qualiopi/evaluations" element={<EvaluationsPage />} />
                <Route path="qualiopi/evaluations/:id" element={<EvaluationDetailPage />} />
                <Route path="qualiopi/evaluations/templates/:id" element={<EvaluationTemplateDetailPage />} />
                <Route path="qualiopi/complaints" element={<ComplaintsPage />} />
                <Route path="qualiopi/complaints/:id" element={<ComplaintDetailPage />} />
                <Route path="qualiopi/improvements" element={<ImprovementsPage />} />
                <Route path="qualiopi/watch" element={<WatchPage />} />
                {/* Ancienne page « Veille » sans données : la veille vit dans qualiopi/watch. */}
                <Route path="qualiopi/monitoring" element={<Navigate to="../watch" relative="path" replace />} />
                <Route path="qualiopi/marketing-kpis" element={<QualiopiMarketingKPIsPage />} />
                <Route path="qualiopi/surveys" element={<SurveyTemplatesPage />} />
                <Route path="qualiopi/surveys/:id" element={<SurveyTemplateDetailPage />} />
                <Route path="qualiopi/surveys/assignments" element={<SurveyAssignmentsPage />} />
                <Route path="qualiopi/simulator" element={<SimulatorPage />} />
                <Route element={<PermissionRoute module="qualiopi" operation="delete" />}>
                  <Route path="qualiopi/complaints/:id/delete" element={<ComplaintDeletePage />} />
                  <Route path="qualiopi/improvements/:id/delete" element={<ImprovementActionDeletePage />} />
                  <Route path="qualiopi/watch/:id/delete" element={<WatchEntryDeletePage />} />
                  <Route path="qualiopi/watch/sources/:id/delete" element={<WatchSourceDeletePage />} />
                  <Route path="qualiopi/surveys/:id/delete" element={<SurveyTemplateDeletePage />} />
                  <Route path="qualiopi/surveys/:id/questions/:questionId/delete" element={<SurveyQuestionDeletePage />} />
                  <Route path="qualiopi/evaluations/templates/:id/questions/:questionId/delete" element={<EvaluationTemplateQuestionDeletePage />} />
                </Route>
              </Route>

              {/* Lieux et référentiels de compétences : droits Formation du serveur. */}
              <Route element={<PermissionRoute module="training" />}>
                <Route path="qualiopi/venues" element={<VenuesPage />} />
                <Route path="admin/competency-frameworks" element={<CompetencyFrameworksPage />} />
                <Route element={<PermissionRoute module="training" operation="delete" />}>
                  <Route path="qualiopi/venues/:id/delete" element={<VenueDeletePage />} />
                  <Route path="admin/competency-frameworks/:id/delete" element={<CompetencyFrameworkDeletePage />} />
                </Route>
              </Route>

              {/* Documents */}
              <Route element={<PermissionRoute module="documents" />}>
                <Route path="documents" element={<DocumentsPage />} />
                <Route path="documents/generate" element={<DocumentGeneratePage />} />
                <Route element={<PermissionRoute module="documents" operation="delete" />}>
                  <Route path="documents/:id/delete" element={<DocumentDeletePage />} />
                </Route>
              </Route>

              {/* Modèles d'e-mails et de documents : module Modèles du serveur. */}
              <Route element={<PermissionRoute module="templates" />}>
                <Route path="admin/email-templates" element={<EmailTemplatesPage />} />
                <Route path="admin/document-templates" element={<DocumentTemplatesPage />} />
                <Route element={<PermissionRoute module="templates" operation="delete" />}>
                  <Route path="admin/document-templates/:id/delete" element={<DocumentTemplateDeletePage />} />
                  <Route path="admin/email-templates/:id/delete" element={<EmailTemplateDeletePage />} />
                </Route>
                <Route element={<PermissionRoute module="templates" operation="transition" />}>
                  <Route path="admin/email-templates/:id/restore" element={<EmailTemplateRestorePage />} />
                </Route>
              </Route>

              {/* HR */}
              <Route element={<PermissionRoute module="hr" />}>
                <Route path="hr/staff" element={<StaffPage />} />
                <Route path="hr/staff/:id" element={<StaffDetailPage />} />
                <Route path="hr/org-chart" element={<OrgChartPage />} />
                <Route path="hr/instructors" element={<InstructorsPage />} />
                <Route path="hr/instructors/:id" element={<InstructorDetailPage />} />
                <Route path="hr/self-verification" element={<SelfVerificationPage />} />
                <Route path="hr/self-verification/:id" element={<SelfVerificationDetailPage />} />
                <Route path="hr/staff-evaluations" element={<StaffEvaluationsPage />} />
                <Route path="hr/staff-evaluations/:id" element={<StaffEvaluationDetailPage />} />
                <Route path="hr/competency-evaluations" element={<StaffEvaluationsPage />} />
                <Route path="hr/professional-interviews" element={<ProfessionalInterviewsPage />} />
                <Route path="hr/vendors" element={<VendorsPage />} />
                <Route element={<PermissionRoute module="hr" operation="write" />}>
                  <Route path="hr/vendors/new" element={<VendorWizardPage />} />
                  {/* La photo se retire avec le droit d'écriture. */}
                  <Route path="hr/instructors/:id/photo/delete" element={<InstructorPhotoDeletePage />} />
                </Route>
                <Route path="hr/vendors/:id" element={<VendorDetailPage />} />
                <Route path="hr/vendor-contracts" element={<VendorContractsPage />} />
                <Route path="hr/vendor-contracts/:id" element={<VendorContractDetailPage />} />
                <Route element={<PermissionRoute module="hr" operation="delete" />}>
                  <Route path="hr/staff/:id/delete" element={<StaffDeletePage />} />
                  <Route path="hr/staff/:id/missions/:missionId/delete" element={<MissionDeletePage />} />
                  <Route path="hr/self-verification/:id/delete" element={<SelfVerificationDeletePage />} />
                  <Route path="hr/staff-evaluations/:id/delete" element={<StaffEvaluationDeletePage />} />
                  <Route path="hr/vendors/:id/delete" element={<VendorDeletePage />} />
                  <Route path="hr/vendor-contracts/:id/delete" element={<VendorContractDeletePage />} />
                  <Route path="hr/instructors/:id/documents/:documentId/delete" element={<InstructorDocumentDeletePage />} />
                  <Route path="hr/instructors/:id/availability-slots/:slotId/delete" element={<InstructorAvailabilitySlotDeletePage />} />
                  <Route path="hr/instructors/:id/skills/:skillId/delete" element={<InstructorSkillDeletePage />} />
                  <Route path="hr/vendors/:id/documents/:documentId/delete" element={<VendorDocumentDeletePage />} />
                  <Route path="hr/vendors/:id/instructors/:instructorId/delete" element={<VendorInstructorDeletePage />} />
                </Route>
              </Route>

              {/* Messaging */}
              <Route element={<PermissionRoute module="messaging" />}>
                <Route path="messages" element={<MessagesPage />} />
                <Route path="messages/:conversationId" element={<MessagesPage />} />
              </Route>

              {/* Admin */}
              <Route element={<PermissionRoute module="admin" />}>
                <Route path="admin/scheduled-jobs" element={<ScheduledJobsPage />} />
                <Route path="admin/audit-log" element={<AuditLogPage />} />
                <Route path="admin/cpf-validation" element={<CpfValidationPage />} />
                <Route path="admin/public-catalog" element={<PublicCatalogPage />} />
                <Route path="admin/email-send-logs" element={<Navigate to="../email-logs" replace />} />
                <Route path="admin/email-logs" element={<EmailAuditLogPage />} />
              </Route>
              {/* Effacement des données personnelles (RGPD art. 17) : Admin › Supprimer. */}
              <Route element={<PermissionRoute module="admin" operation="delete" />}>
                <Route path="crm/contacts/:id/erase" element={<ContactErasurePage />} />
                <Route path="hr/instructors/:id/erase" element={<InstructorErasurePage />} />
                <Route path="training/juries/:id/members/:memberId/erase" element={<JuryMemberErasurePage />} />
              </Route>

              {/* Help */}
              <Route path="help" element={<HelpPage />} />

              {/* Settings — personal profile stays open to every role; the org
                  admin OPCO-rules editor is gated on the settings module. */}
              <Route path="settings" element={<SettingsPage />} />
              <Route element={<PermissionRoute module="admin" operation="delete" />}>
                <Route path="settings/team/:id/delete" element={<TeamMemberDeletePage />} />
              </Route>
              {/* Résiliation : lecture Admin, programmation Admin › Supprimer. */}
              <Route element={<PermissionRoute module="admin" />}>
                <Route path="settings/organization/termination" element={<OrganizationTerminationPage />} />
                <Route element={<PermissionRoute module="admin" operation="delete" />}>
                  <Route path="settings/organization/termination/delete" element={<OrganizationTerminationDeletePage />} />
                </Route>
                <Route element={<PermissionRoute module="admin" operation="write" />}>
                  <Route path="settings/organization/signature-assets/:kind/delete" element={<SignatureAssetDeletePage />} />
                </Route>
              </Route>
              <Route element={<PermissionRoute module="settings" />}>
                <Route path="settings/opco-rules" element={<OpcoRulesPage />} />
                <Route path="admin/email-outbox" element={<EmailOutboxPage />} />
                <Route element={<PermissionRoute module="settings" operation="delete" />}>
                  <Route path="settings/opco-rules/:id/delete" element={<OpcoRuleDeletePage />} />
                </Route>
              </Route>
              <Route path="settings/*" element={<SettingsPage />} />

              {/* Reports */}
              <Route element={<PermissionRoute module="reports" />}>
                <Route path="reports/bpf" element={<BPFReportPage />} />
                <Route path="reports/statistics" element={<StatisticsPage />} />
                {/* URL stays admin/exports; the backend exports handlers enforce
                    the reports module, so the route is gated on reports (not admin). */}
                <Route path="admin/exports" element={<ExportsPage />} />
                <Route path="admin/marketing-kpis" element={<MarketingKPIsPage />} />
                <Route element={<PermissionRoute module="reports" operation="delete" />}>
                  <Route path="admin/exports/:id/delete" element={<ExportDeletePage />} />
                  <Route path="admin/marketing-kpis/:id/delete" element={<MarketingKpiDeletePage />} />
                </Route>
              </Route>
            </Route>

            {/* OAuth consent screen (MCP) */}
            <Route path="/app/oauth/authorize" element={<OAuthConsentPage />} />

            {/* Catalogue public : ancienne route SPA → redirige vers /c/:slug
                (catalogue servi en SSR par le backend). Le 301 SEO-propre est
                géré côté nginx/backend, hors scope SPA — cette redirection
                client couvre les liens déjà ouverts dans un onglet. */}
            <Route path="/public/:slug/catalog" element={<LegacyCatalogRedirect />} />

            {/* Jury Portal (public, token-based access) */}
            <Route path="/jury/:token" element={<JuryPortalPage />} />

            {/* Public program viewer (no auth, lien public catalogue programme) */}
            <Route path="/public/programs/:token" element={<PublicProgramPage />} />

            {/* Public quote viewer + needs assessment (no auth, lien public devis) */}
            <Route path="/q/:token" element={<PublicQuotePage />} />
            <Route path="/q/:token/needs" element={<PublicNeedsFormPage />} />

            {/* Public quote amendment viewer (no auth, lien public avenant) */}
            <Route path="/qa/:token" element={<PublicQuoteAmendmentPage />} />

            {/* Questionnaire de satisfaction public (lien de l'email d'enquête) */}
            <Route path="/survey/:token" element={<PublicSurveyPage />} />

            {/* Public invoice viewer (no auth, magic-link facture client) */}
            <Route path="/i/:token" element={<PublicInvoicePage />} />

            {/* Public job description signature (no auth, /jd/:token) */}
            <Route path="/jd/:token" element={<PublicJobDescriptionPage />} />

            {/* Public vendor contract signature (no auth, /vc/:token) + verify */}
            <Route path="/vc/:token" element={<PublicVendorContractPage />} />
            <Route path="/verify/:token" element={<VerifyVendorContractPage />} />

            {/* Public attendance QR signing (no auth, QR projeté en salle) */}
            <Route path="/a/:token" element={<PublicAttendanceSignPage />} />

            {/* Public session instructor invitation (no auth, accept/decline) */}
            <Route path="/formateur/invitation/:token" element={<PublicInstructorInvitationPage />} />

            {/* Portal routes (public login) */}
            <Route path="/portal/login" element={<PortalLoginPage />} />

            {/* Portal routes (protected) */}
            <Route
              element={
                <PortalProtectedRoute>
                  <PortalLayout />
                </PortalProtectedRoute>
              }
            >
              <Route path="/portal" element={<Navigate to="/portal/dashboard" replace />} />
              <Route path="/portal/dashboard" element={<PortalDashboardPage />} />
              <Route path="/portal/sessions" element={<PortalSessionsPage />} />
              <Route path="/portal/sessions/:registrationId" element={<PortalSessionDetailPage />} />
              <Route path="/portal/attendance" element={<PortalAttendancePage />} />
              <Route path="/portal/attendance/:attendanceId/sign" element={<PortalAttendanceSignPage />} />
              <Route path="/portal/evaluations" element={<PortalEvaluationsPage />} />
              <Route path="/portal/documents" element={<PortalDocumentsPage />} />
              <Route path="/portal/profile" element={<PortalProfilePage />} />
              <Route path="/portal/profile/health-consent/withdraw" element={<PortalHealthConsentWithdrawPage />} />
            </Route>

            {/* Root redirect: authenticated -> /:orgSlug/dashboard, else -> /login */}
            <Route path="/" element={<RootRedirect />} />

            {/* Bare deep-links `/training/programs`, `/commercial/quotes`, etc.
                Un segment litéral bat `/:orgSlug` en rank React Router — sans
                ces routes explicites, `/:orgSlug` matcherait parasitement
                `orgSlug="training"` et rendrait un DashboardLayout fantôme.
                APP_PATH_PREFIXES centralise la liste, testée exhaustive. */}
            {APP_PATH_PREFIXES.map((prefix) => (
              <Route
                key={prefix}
                path={`/${prefix}/*`}
                element={<BarePathRedirect />}
              />
            ))}

            {/* 404 */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
          </Suspense>
          </RouteErrorBoundary>
          <Toaster />
          <PlanLimitDialogHost />
          <ProgressToastContainer />
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
    </ThemeProvider>
  )
}

export default App
